<template>
	<ion-modal :is-open="showModal" @ion-modal-did-dismiss="onClose">
		<ion-header>
			<ion-toolbar color="branding">
				<div class="text-center text-xl">
					{{ $t("pwa.install-seedgreen") }}
				</div>
			</ion-toolbar>
		</ion-header>
		<div class="p-3">
			<div class="mb-4 pb-2 pt-4 text-center">
				{{ $t("pwa.seedgreen-can-be-installed") }}
				<br />
				{{ $t("pwa.no-app-store-download") }}
			</div>
			<img
				src="./images/beta-ios-installed.jpg"
				alt="SeedGreen iOS installed"
				class="mb-4 rounded-xl"
			/>

			<div class="flex">
				<ion-button class="flex-1" color="light" @click="dismiss">
					{{
						wasManuallyShown
							? $t("common.close")
							: $t("common.dismiss")
					}}
				</ion-button>

				<ion-button
					v-if="isInstallable && false"
					class="flex-1"
					@click="install"
				>
					{{ $t("pwa.install") }}
				</ion-button>
				<ion-button v-else class="flex-1" @click="showInstructions">
					{{ $t("pwa.show-me-how") }}
				</ion-button>
			</div>
		</div>
	</ion-modal>
</template>

<script setup lang="ts">
	import {
		IonHeader,
		IonToolbar,
		IonModal,
		IonButton,
		modalController,
	} from "@ionic/vue";
	import { computed, defineAsyncComponent, onMounted, ref } from "vue";
	import { useDevicePlatform } from "@/composables/useDevicePlatform";
	import {
		isInstallPromptTimedOut,
		isStandalone,
		timeoutInstallPrompt,
	} from "@/utils/pwa";
	import { useVerifyToken } from "seedgreen-shared/query/verify-token";

	const { tokenValid } = useVerifyToken();

	const showModal = computed(() => {
		/**
		 * Logic:
		 * Logged in
		 * Not PWA
		 * Is mobile
		 * Not dismissed
		 */
		return (
			(tokenValid.value || wasManuallyShown.value) &&
			!isStandalone() &&
			isMobile.value &&
			!isDismissed.value
		);
	});
	const { isMobile, isIOS } = useDevicePlatform();
	const isDismissed = ref(isInstallPromptTimedOut());
	const isInstallable = computed(() => window.deferredPrompt !== null);
	const wasManuallyShown = ref(false);

	defineExpose({ show });

	function show() {
		wasManuallyShown.value = true;
		isDismissed.value = false;
	}

	function dismiss() {
		isDismissed.value = true;

		if (!wasManuallyShown.value) {
			timeoutInstallPrompt();
		}
	}

	function onClose() {
		isDismissed.value = true;
	}

	onMounted(() => {
		// Close on install
		window.addEventListener("appinstalled", () => {
			onClose();
		});
	});

	/** Install using the native install method */
	async function install() {
		if (!window.deferredPrompt) return;
		await window.deferredPrompt.prompt();
	}

	async function showInstructions() {
		isDismissed.value = true;

		if (isIOS.value) {
			await showiOSInstructions();
		} else {
			await showAndroidInstructions();
		}
	}

	async function showiOSInstructions() {
		const modal = await modalController.create({
			component: defineAsyncComponent(
				() => import("./iOSInstallInstructions.vue"),
			),
			cssClass: "install-modal",
		});
		return modal.present();
	}

	async function showAndroidInstructions() {
		const modal = await modalController.create({
			component: defineAsyncComponent(
				() => import("./AndroidInstallInstructions.vue"),
			),
			cssClass: "install-modal",
		});
		return modal.present();
	}
</script>

<style scoped>
	ion-modal {
		--height: auto;
		--border-radius: 0.75rem;
	}
</style>

<style>
	.install-modal {
		--height: auto;
		--border-radius: 0.75rem;
	}
</style>
